import React, { useContext, useEffect, useState } from 'react';
import { labels, SELECT, TRAFFIC_DISTRIBUTION } from '../../utils/Constants';
import Slider from '../../components/Slider';
import { toast } from 'react-toastify';
import { ABContext } from '../../contexts/ABContext';
import { getLiveApplets, getModelsBasedOnChannelKey } from '../../services/ABServices';
import { TModel, TModelData } from '../../types/ab';
import { AppContext } from 'Context/AppContext';
import { CoSellerModelIcon, SubstitutionModelIcon } from 'assests/GmsIcons';
import Select from 'react-select';
import selectStyles from '../../utils/Styles';
import ConfirmDialog from './ConfirmDialog';

const labelNames = [
    'Model A (Control)',
    'Model B (Variant)',
    'Model C (Variant)',
    'Model D (Variant)',
];

const TrafficDistributionSection = () => {
    const abContext = useContext(ABContext);
    const appContext = useContext(AppContext);
    const { defaultModels, models, setModels, modelData, setModelData, liveApplets, setLiveApplets, setDefaultModels } = useContext(ABContext);
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [dialogDisplayed, setDialogDisplayed] = useState<boolean>(false);
    const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

    useEffect(() => {
        if (abContext?.selectedApplet) {
            getModels();
            setDialogDisplayed(false)
        }
    }, [abContext?.selectedApplet]);

    useEffect(() => {
        const applets = abContext?.payload?.applets;

        if (applets && applets.length > 0) {
            const firstApplet = applets[0];
            const firstAppletId = firstApplet?.applet_id;
            abContext?.setSelectedApplet(firstAppletId);

            const userSavedModels = applets[0]?.models;

            if (userSavedModels?.length > 0) {
                processAndSetModels(userSavedModels);
            } else {
                selectDefaultModel(modelData, firstAppletId);
            }
        }
    }, [abContext?.payload?.applets]);

    const processAndSetModels = async (modelsToProcess: TModel[]) => {
        const freshModelData = await getModels();

        if (freshModelData) {

            const processedModels = modelsToProcess.map((model) => {
                let modelType = model.type;

                if (!modelType) {
                    const matchedModel = freshModelData.find((m: TModel) => m.model_id === model.model_id);
                    if (matchedModel && matchedModel.type) {
                        modelType = matchedModel.type;
                    }
                }

                return {
                    label: model.label,
                    is_control: model.is_control,
                    model_id: model.model_id,
                    model_name: model.model_name,
                    traffic_distribution: model.traffic_distribution,
                    type: modelType
                };
            });

            setModels(processedModels);
        } else {
            setModels(modelsToProcess);
        }
    };


    const showConfirmDialog = (onConfirm: () => void) => {
        setPendingAction(() => onConfirm);
        setShowDialog(true);
    };

    const handleConfirm = () => {
        setDialogDisplayed(true)
        if (pendingAction) pendingAction();
        setShowDialog(false);
    };

    const handleCancel = () => {
        setShowDialog(false);
    };


    const getModels = async () => {
        try {
            if (!appContext?.channelKey) return
            const response = await getModelsBasedOnChannelKey(appContext?.channelKey);
            if (response?.done) {
                setModelData(response?.data);
                return response?.data;
            }
            return null;
        } catch (error) {
            console.error("getModels ~ error:", error);
            return null;
        }
    }

    const fetchLiveAppletsAndDefaultModels = async () => {
        if (!appContext?.channelKey) return
        try {
            const liveAppletsResponse = await getLiveApplets(appContext?.channelKey)
            setLiveApplets(liveAppletsResponse?.data?.ab_test_applet || []);
            setDefaultModels(liveAppletsResponse?.data?.default_models || {})
            return liveAppletsResponse?.data?.default_models;
        } catch (error) {
            setLiveApplets([]);
            setDefaultModels({});
            toast.error('Failed to fetch live applets');
        }
    }

    const selectDefaultModel = async (inputData: TModelData[] | undefined, selectedAppletId: string) => {

        let modelDataToUse = inputData;
        if (!modelDataToUse || modelDataToUse.length === 0) {
            try {
                if (!appContext?.channelKey) {
                    return;
                }

                const response = await getModelsBasedOnChannelKey(appContext.channelKey);

                if (!response?.done || !response?.data || response?.data.length === 0) {
                    console.error("Failed to fetch models or no models returned");
                    return;
                }

                setModelData(response.data);
                modelDataToUse = response.data;
            } catch (error) {
                console.error("Error fetching models:", error);
                return;
            }
        }

        let currentDefaultModels = defaultModels;
        const hasDefaultModelForApplet = currentDefaultModels &&
            Object.keys(currentDefaultModels).length > 0 &&
            currentDefaultModels[selectedAppletId] !== undefined;

        if (!hasDefaultModelForApplet) {
            currentDefaultModels = await fetchLiveAppletsAndDefaultModels();

            if (!currentDefaultModels || !currentDefaultModels[selectedAppletId]) {
                return;
            }
        }

        const defaultModelIdForSelectedApplet = currentDefaultModels[selectedAppletId];

        const defaultModel: TModelData | undefined = modelDataToUse?.find(
            (e: TModelData) => e?.model_id === defaultModelIdForSelectedApplet
        );

        if (!defaultModel) {
            return;
        }

        setModels([{
            label: labels[0],
            traffic_distribution: 100,
            model_id: defaultModel.model_id,
            is_control: true,
            model_name: defaultModel.model_name,
            type: defaultModel.type
        }]);

        return defaultModel;
    };

    const removeModel = (modelId: string | null) => {
        setModels((prevModels: TModel[]) => {
            if (prevModels.length === 1) {
                toast.warn("At least one model should be present.");
                return prevModels;
            }
            const updatedModels = prevModels.filter((model, index) => {
                if (modelId === null) {
                    return index !== prevModels.length - 1;
                }
                return model.model_id !== modelId;
            });

            const equalPercentage = parseFloat((100 / updatedModels.length).toFixed(1));
            let totalPercentage = equalPercentage * (updatedModels.length - 1);
            const lastModelPercentage = parseFloat((100 - totalPercentage).toFixed(1));

            return updatedModels.map((model, index) => ({
                ...model,
                traffic_distribution: index < updatedModels.length - 1 ? equalPercentage : lastModelPercentage
            }));
        });
    };

    const addModel = () => {
        if (models.length < 4) {
            const newModel: TModel = { label: labels[models.length], traffic_distribution: 0, model_id: '', is_control: false, model_name: "" };
            setModels((prevModels: TModel[]) => {
                const updatedModels = [...prevModels, newModel];
                const equalPercentage = parseFloat((100 / updatedModels.length).toFixed(1));
                let totalPercentage = equalPercentage * (updatedModels.length - 1);
                const lastModelPercentage = parseFloat((100 - totalPercentage).toFixed(1));

                return updatedModels.map((model, index) => ({
                    ...model,
                    traffic_distribution: index < updatedModels.length - 1 ? equalPercentage : lastModelPercentage
                }));
            });
        } else {
            toast.warn("You've reached the model limit.");
        }
    };

    const handleSliderChange = (index: number, value: number) => {
        setModels((prevModels: TModel[]) => {
            const totalPercentage = prevModels.reduce((acc, model) => acc + model.traffic_distribution, 0);
            const remainingPercentage = totalPercentage - prevModels[index].traffic_distribution;

            let newModels = prevModels.map((model, i) => {
                if (i === index) {
                    return { ...model, traffic_distribution: value };
                }
                return model;
            });

            if (remainingPercentage > 0) {
                const scaleFactor = (100 - value) / remainingPercentage;
                newModels = newModels.map((model, i) => {
                    if (i !== index) {
                        return { ...model, traffic_distribution: model.traffic_distribution * scaleFactor };
                    }
                    return model;
                });
            } else if (value < 100) {
                const otherModelsCount = prevModels.length - 1;
                const equalPercentage = (100 - value) / otherModelsCount;
                newModels = newModels.map((model, i) => {
                    if (i !== index) {
                        return { ...model, traffic_distribution: equalPercentage };
                    }
                    return model;
                });
            }

            return newModels.map(model => ({
                ...model,
                traffic_distribution: parseFloat(model.traffic_distribution.toFixed(1))
            }));
        });
    };

    const handleModelSelect = (index: number, modelName: string, selectedModelId: string, type?: "Co-seller" | "Substitution") => {
        if (index === 0 && models?.length > 0 && models[0]?.model_name && !dialogDisplayed && defaultModels[abContext?.selectedApplet]) {
            showConfirmDialog(() => {
                setModels((prevModels: TModel[]) =>
                    prevModels.map((model, i) =>
                        i === index ? { ...model, model_name: modelName, type: type, model_id: selectedModelId, traffic_distribution: index === 0 && prevModels?.length === 0 ? 100 : model?.traffic_distribution } : model
                    )
                );
            });
            return;
        }
        setModels((prevModels: TModel[]) =>
            prevModels.map((model, i) =>
                i === index ? { ...model, model_name: modelName, type: type, model_id: selectedModelId, traffic_distribution: index === 0 && prevModels?.length === 0 ? 100 : model?.traffic_distribution } : model
            )
        );
    };

    const getAvailableModels = (currentIndex: number) => {
        const selectedModelIds = models.map((model: TModel) => model.model_id);
        return modelData.filter((model: TModel) =>
            !selectedModelIds.includes(model.model_id.toString()) ||
            model.model_id.toString() === models[currentIndex].model_id
        );
    };

    const hasAvailableModelsToSelect = () => {
        const selectedModelIds = models.map((model: TModel) => model.model_id);
        return modelData.some((model: TModel) => !selectedModelIds.includes(model.model_id.toString()));
    };
    
    return (<>
        <section className='tw-pt-2 tw-pb-6 tw-border-b'>
            {models?.map((model: TModel, index: number) => (
                <div key={model.label} className='tw-flex tw-items-center tw-gap-8 tw-py-2'>
                    <div className="tw-w-[20%]">
                        <label className="text-caption-style tw-text-[#747593]">{labelNames[index]}</label>
                        <Select
                            className="shadow-none"
                            classNamePrefix="custom-select"
                            value={model?.model_id ? {
                                value: model.model_id,
                                label: model.model_name,
                                type: model.type
                            } : null}
                            onChange={(selected: any) => {
                                if (selected) {
                                    handleModelSelect(index, selected.label, selected.value, selected.type);
                                }
                            }}
                            options={[
                                { value: "", label: SELECT },
                                ...getAvailableModels(index).map((availableModel: TModel) => ({
                                    value: availableModel.model_id,
                                    label: availableModel.model_name,
                                    type: availableModel.type
                                }))
                            ]}
                            formatOptionLabel={(option: any) => (
                                <div className="tw-flex tw-items-center">
                                    {option.value && (
                                        <span className="tw-mr-2">
                                            {option.type === 'Co-seller' ? <CoSellerModelIcon /> :
                                                option.type === 'Substitution' ? <SubstitutionModelIcon /> : null}
                                        </span>
                                    )}
                                    <span>{option.label}</span>
                                </div>
                            )}
                            styles={selectStyles}
                        />
                    </div>
                    <div className="tw-w-[20%] tw-self-stretch">
                        <label className="text-caption-style tw-text-[#747593] tw-mt-1">{index === 0 ? TRAFFIC_DISTRIBUTION : ''}</label>
                        <div className='tw-mt-1'>
                            <Slider
                                disable={false}
                                value={model?.traffic_distribution || 0}
                                onChange={(e) => handleSliderChange(index, Math.floor(Number(e.target.value)))}
                            />
                        </div>
                    </div>
                    <div className="tw-w-[8%] tw-self-stretch">
                        <label className="text-caption-style tw-text-[#747593]">{''}</label>
                        <input
                            type="text"
                            className="shadow-none drop-down-dm tw-ps-2"
                            id="percentage"
                            value={model ? `${model.traffic_distribution}%` : '0%'}
                            placeholder="50%"
                            readOnly
                        />
                    </div>
                    {index !== 0 && (
                        <button className="tw-text-[#c03939] tw-text-sm tw-mt-6" onClick={() => removeModel(model?.model_id)}>Remove</button>
                    )}
                </div>
            ))}
            {models.length < 4 && models[models.length - 1]?.model_id && hasAvailableModelsToSelect() && (
                <button className='tw-text-[#091CAA] tw-mt-3 tw-text-sm' onClick={addModel}>+ Models</button>
            )}
        </section>
        {showDialog && <ConfirmDialog
            title={liveApplets && liveApplets?.length > 0 && liveApplets?.includes(abContext?.selectedApplet) ? 'Control Model Change' : 'Change in Promoted Model'}
            message={liveApplets && liveApplets?.length > 0 && liveApplets?.includes(abContext?.selectedApplet) ?
                `"${models[0].model_name}" is currently the control model in an existing AB test. Do you want to change it?`
                : `"${models[0].model_name}" is currently promoted with 100% traffic in Commend. Selecting a different control model will stop ${models[0].model_name}. Continue?`
            }
            onConfirm={handleConfirm}
            onClose={handleCancel}
            confirmText="Yes, Continue"
            cancelText="Cancel"
        />}
    </>
    );

}

export default TrafficDistributionSection;